import { CommonModule } from '@angular/common';
import { Component, Inject, Injectable } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Event, NavigationEnd, Router } from '@angular/router';
import { InformationBanner, InformationBannerApiService } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { isAfter, isBefore } from 'date-fns';
import { concatMap, from, of, timer } from 'rxjs';
import { ClientEnvironment, ENVIRONMENT } from '../environment';
import { USER_SESSION_TOKEN } from '../user-access/user-session.token';
import { StorageUtilityLib } from '../util/storage-utility';

const storageKey = 'userShowedInfoBanner';
const skipBannerLocalKey = 'skipBannerLocal';
const skipBannerSessionKey = 'skipBannerSession';

@Injectable({ providedIn: 'root' })
export class InformationBannerService {
  private running = false;
  private _pause = false;

  private skipBannersLocal?: { [key: string]: boolean };
  private skipBannersSession?: { [key: string]: boolean };

  constructor(
    @Inject(ENVIRONMENT) private env: ClientEnvironment,
    private router: Router,
    private dialog: MatDialog,
    private infoBannerService: InformationBannerApiService,
    private storageUtility: StorageUtilityLib,
  ) {
    this.skipBannersLocal = this.storageUtility.getLocalStorageNullable<{ [key: string]: boolean }>(skipBannerLocalKey) || undefined;
  }

  /**
   * Typically called from the login page. The banners here do not need authentication.
   */
  loginBanner() {
    if (this.env.infoBanner?.enabled) {
      this.infoBannerService.informationBannerControllerGetLoginBanners(this.getExcludeBanners()).subscribe((banners) => {
        if (banners) {
          from(banners)
            .pipe(
              concatMap((banner) => {
                if (!this.skipBannersLocal?.[banner._id]) return this.openInformationBanner(banner);
                else return of(null);
              }),
            )
            .subscribe();
        }
      });
    }
  }

  /**
   * Start the banner subscription. On every (main) route change, the banners are checked and displayed if necessary.
   */
  run() {
    if (this.running) {
      return;
    }
    this.running = true;

    if (this.env.infoBanner?.enabled) {
      let lastRoute = '/login';
      this.router.events.subscribe((event: Event) => {
        if (event instanceof NavigationEnd && !event.url.includes('?')) {
          if (!this._pause && !event.url.startsWith(lastRoute)) {
            lastRoute = event.url.split('/')[1];
            timer(600).subscribe(() => {
              // the context (routing path) could be used to show different banners on different pages
              this.showInfoBanners();
            });
          }
        }
      });
    }
  }

  /**
   * Stop showing banners until you resume
   */
  pause() {
    this._pause = true;
  }

  /**
   * Resume showing banners
   */
  resume() {
    this._pause = false;
    this.showInfoBanners();
  }

  private showInfoBanners() {
    if (this._pause) {
      return;
    }

    this.skipBannersSession = this.storageUtility.getSessionStorageNullable<{ [key: string]: boolean }>(skipBannerSessionKey) || undefined;

    this.infoBannerService.informationBannerControllerGetBanners(this.getExcludeBanners()).subscribe((banners) => {
      if (banners) {
        from(banners)
          .pipe(
            concatMap((banner) => {
              if (!this.skipBannersLocal?.[banner._id] && !this.skipBannersSession?.[banner._id]) {
                return this.openInformationBanner(banner);
              } else {
                return of(null);
              }
            }),
          )
          .subscribe();
      }
    });
  }

  private getExcludeBanners() {
    const showedBanners = this.storageUtility.getLocalStorageNullable<string[]>(storageKey);
    return showedBanners ? showedBanners.join(',') : undefined;
  }

  private openInformationBanner(banner: InformationBanner) {
    return this.dialog
      .open(InformationBannerComponent, {
        width: '100%',
        maxWidth: '700px',
        position: { top: '50px' },
        data: { banner: banner },
      })
      .afterClosed();
  }

  getBannerStatus(publishDateTime: Date | string, unPublishDateTime: Date | string) {
    if (publishDateTime && unPublishDateTime) {
      const now = new Date();
      if (isAfter(now, new Date(publishDateTime)) && isBefore(now, new Date(unPublishDateTime))) {
        return $localize`Published`;
      }
      return $localize`Unpublished`;
    }
    return '';
  }
}

@Component({
  template: `
    <div class="modal-body relative">
      <header class="header modal-header flex flex-col gap-2 place-content-between">
        <div class="flex justify-between">
          <div class="logo-container flex flex-col w-full place-content-center logo-padding" *ngIf="logoUrl">
            <a class="h-full w-full flex flex-col justify-center items-center" rel="noopener">
              <img class="max-h-[70px]" [src]="logoUrl" alt="Vorwerk" />
            </a>
          </div>
          <button mat-icon-button mat-dialog-close class="button-close" (click)="closeBanner(banner)">
            <mat-icon class="material-icons-outlined" aria-hidden="true">close</mat-icon>
            <span class="sr-only" i18n>Close</span>
          </button>
        </div>
        <div class="flex justify-between mt-2">
          <h4 class="info-banner-headline text-center w-full">{{ banner.headLine }}</h4>
        </div>
      </header>
      <div class="flex-col gap-3 bg-white h-full">
        <div class="modal-main flex flex-col !min-h-full">
          <div class="flex flex-col mx-4" [innerHTML]="banner.descriptionText"></div>
          <div class="mx-2">
            <mat-checkbox class="mt-4" (change)="onCheckboxClick($event.checked, banner)">
              <span i18n>Do not show this message again</span>
            </mat-checkbox>
          </div>
        </div>
      </div>
      <div class="modal-footer flex justify-center w-full gap-4 !items-center">
        <a class="button-primary" *ngIf="banner.linkRef" [href]="banner.linkRef" target="_blank" [attr.data-test-id]="'asset-upload_button_upload'">
          {{ banner.linkText ? banner.linkText : 'More info' }}
        </a>
      </div>
    </div>
  `,
  styles: [
    `
      .logo-padding {
        padding-block: 1rem;
      }
      .info-banner-headline {
        font-size: 1.5rem;
      }
    `,
  ],
  standalone: true,
  imports: [CommonModule, MatIconModule, MatCheckboxModule, MatDialogModule],
})
export class InformationBannerComponent {
  banner!: InformationBanner;
  logoUrl?: string;

  constructor(
    @Inject(ENVIRONMENT) private env: ClientEnvironment,
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    @Inject(MAT_DIALOG_DATA) protected data: { banner: InformationBanner },
    private storageUtility: StorageUtilityLib,
    private infoBannerService: InformationBannerApiService,
  ) {
    this.banner = this.data.banner;
    this.logoUrl = this.env.infoBanner?.logoSrc;
  }

  getSalutation() {
    const userSession: AnyType = this.storageUtility.getLocalStorage(this.userSessionToken);
    if (userSession?.userData) {
      return this.env.infoBanner?.salutationFunction ? this.env.infoBanner.salutationFunction(userSession) : `Hi, ${userSession.userData.firstName}!`;
    } else {
      return '';
    }
  }

  onCheckboxClick(checked: boolean, banner: InformationBanner) {
    if (checked) {
      this.setShowedBanner(banner._id);
    }
    const skipBanners = this.storageUtility.getLocalStorageNullable<{ [key: string]: boolean }>(skipBannerLocalKey) || undefined;
    this.storageUtility.setLocalStorage(skipBannerLocalKey, { ...skipBanners, [banner._id]: checked });
  }

  closeBanner(banner: InformationBanner) {
    const closedBanners = this.storageUtility.getSessionStorageNullable<{ [key: string]: boolean }>(skipBannerSessionKey) || undefined;
    this.storageUtility.setSessionStorage(skipBannerSessionKey, { ...closedBanners, [banner._id]: true });
  }

  private setShowedBanner(showedBanner: string) {
    const showedInfoBanners = this.storageUtility.getLocalStorageNullable<string[]>(storageKey) ?? [];
    if (showedInfoBanners.length === 0) {
      showedInfoBanners.push(showedBanner);
      this.storageUtility.setLocalStorage(storageKey, showedInfoBanners);
    } else {
      if (!showedInfoBanners.includes(showedBanner)) {
        // cleanup: load all published ids from backend and remove all of them from showedInfoBanners that are not in the list
        this.infoBannerService.informationBannerControllerGetPublishedIds().subscribe((publishedIds) => {
          const cleanedShowedBanners = showedInfoBanners.filter((banner) => publishedIds.includes(banner));
          cleanedShowedBanners.push(showedBanner);
          this.storageUtility.setLocalStorage(storageKey, cleanedShowedBanners);
        });
      }
    }
  }
}
